import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PLAN_COMMERCIAL_NAMES, PLAN_PRICING, centsToReais } from '@/constants/plans';

export interface PlanStatsItem {
  users: number;
  revenue: number;
  conversions30d: number;
}

export interface PlanStatsData {
  sponsors: {
    intro: PlanStatsItem;
    inspire: PlanStatsItem;
    infinity: PlanStatsItem;
  };
  sponsored: {
    intro: PlanStatsItem;
    infinity: PlanStatsItem;
  };
  totals: {
    totalUsers: number;
    totalPaying: number;
    monthlyRecurringRevenue: number;
    conversionRate: number;
  };
}

export interface PlanActivity {
  id: string;
  user: string;
  action: 'upgrade' | 'downgrade';
  fromPlan: string;
  toPlan: string;
  fromCommercial: string;
  toCommercial: string;
  date: string;
  amount: number;
  billingPeriod: string;
}

export const useAdminPlansData = () => {
  // Fetch user counts by plan type
  const { data: planCounts, isLoading: countsLoading } = useQuery({
    queryKey: ['admin-plan-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('plan_type, role, billing_period')
        .not('plan_type', 'is', null);

      if (error) throw error;

      const counts = {
        sponsor_free: 0,
        sponsor_standard: 0,
        sponsor_premium: 0,
        sponsored_free: 0,
        sponsored_premium: 0,
        // Legacy plan types
        free: 0,
        standard: 0,
        premium: 0,
      };

      data?.forEach(profile => {
        const planType = profile.plan_type as keyof typeof counts;
        if (counts[planType] !== undefined) {
          counts[planType]++;
        }
      });

      return counts;
    }
  });

  // Fetch revenue from plan_changes (confirmed payments)
  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['admin-plan-revenue'],
    queryFn: async () => {
      // Get last 30 days of confirmed plan changes
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('plan_changes')
        .select('new_plan, amount, billing_period, status, created_at')
        .eq('status', 'active')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;

      const revenue = {
        sponsor_standard: 0,
        sponsor_premium: 0,
        sponsored_premium: 0,
      };

      data?.forEach(change => {
        const plan = change.new_plan as keyof typeof revenue;
        if (revenue[plan] !== undefined) {
          // Amount is in cents
          revenue[plan] += change.amount || 0;
        }
      });

      return revenue;
    }
  });

  // Fetch conversions (upgrades from free to paid in last 30 days)
  const { data: conversionsData, isLoading: conversionsLoading } = useQuery({
    queryKey: ['admin-plan-conversions'],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('plan_changes')
        .select('old_plan, new_plan, created_at')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .eq('status', 'active');

      if (error) throw error;

      const conversions = {
        sponsor_standard: 0,
        sponsor_premium: 0,
        sponsored_premium: 0,
      };

      data?.forEach(change => {
        const oldPlan = change.old_plan || '';
        const newPlan = change.new_plan;
        
        // Count only upgrades from free plans
        if (oldPlan.includes('free') || oldPlan === 'free' || !oldPlan) {
          if (newPlan === 'sponsor_standard') conversions.sponsor_standard++;
          if (newPlan === 'sponsor_premium') conversions.sponsor_premium++;
          if (newPlan === 'sponsored_premium') conversions.sponsored_premium++;
        }
      });

      return conversions;
    }
  });

  // Fetch total users
  const { data: totalUsers, isLoading: totalLoading } = useQuery({
    queryKey: ['admin-total-users-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (error) throw error;
      return count || 0;
    }
  });

  // Fetch recent activity
  const { data: recentActivity, isLoading: activityLoading } = useQuery({
    queryKey: ['admin-plan-activity'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plan_changes')
        .select(`
          id,
          old_plan,
          new_plan,
          amount,
          billing_period,
          created_at,
          profiles!plan_changes_user_id_fkey(username)
        `)
        .order('created_at', { ascending: false })
        .limit(15);

      if (error) throw error;

      return data?.map(change => {
        const oldPlan = change.old_plan || 'free';
        const newPlan = change.new_plan;
        
        // Determine if upgrade or downgrade
        const planHierarchy: Record<string, number> = {
          'free': 0,
          'sponsor_free': 0,
          'sponsored_free': 0,
          'standard': 1,
          'sponsor_standard': 1,
          'premium': 2,
          'sponsor_premium': 2,
          'sponsored_premium': 2,
        };

        const isUpgrade = (planHierarchy[newPlan] || 0) > (planHierarchy[oldPlan] || 0);

        return {
          id: change.id,
          user: (change.profiles as any)?.username || 'Usuário',
          action: isUpgrade ? 'upgrade' : 'downgrade',
          fromPlan: oldPlan,
          toPlan: newPlan,
          fromCommercial: PLAN_COMMERCIAL_NAMES[oldPlan as keyof typeof PLAN_COMMERCIAL_NAMES] || oldPlan,
          toCommercial: PLAN_COMMERCIAL_NAMES[newPlan as keyof typeof PLAN_COMMERCIAL_NAMES] || newPlan,
          date: new Date(change.created_at).toLocaleDateString('pt-BR'),
          amount: centsToReais(change.amount || 0),
          billingPeriod: change.billing_period || 'monthly',
        };
      }) as PlanActivity[] || [];
    }
  });

  // Calculate aggregated stats
  const planStats: PlanStatsData = {
    sponsors: {
      intro: {
        users: (planCounts?.sponsor_free || 0) + (planCounts?.free || 0),
        revenue: 0,
        conversions30d: 0,
      },
      inspire: {
        users: (planCounts?.sponsor_standard || 0) + (planCounts?.standard || 0),
        revenue: centsToReais(revenueData?.sponsor_standard || 0),
        conversions30d: conversionsData?.sponsor_standard || 0,
      },
      infinity: {
        users: (planCounts?.sponsor_premium || 0) + (planCounts?.premium || 0),
        revenue: centsToReais(revenueData?.sponsor_premium || 0),
        conversions30d: conversionsData?.sponsor_premium || 0,
      },
    },
    sponsored: {
      intro: {
        users: planCounts?.sponsored_free || 0,
        revenue: 0,
        conversions30d: 0,
      },
      infinity: {
        users: planCounts?.sponsored_premium || 0,
        revenue: centsToReais(revenueData?.sponsored_premium || 0),
        conversions30d: conversionsData?.sponsored_premium || 0,
      },
    },
    totals: {
      totalUsers: totalUsers || 0,
      totalPaying: 
        (planCounts?.sponsor_standard || 0) + 
        (planCounts?.sponsor_premium || 0) + 
        (planCounts?.sponsored_premium || 0) +
        (planCounts?.standard || 0) +
        (planCounts?.premium || 0),
      monthlyRecurringRevenue: centsToReais(
        (revenueData?.sponsor_standard || 0) + 
        (revenueData?.sponsor_premium || 0) + 
        (revenueData?.sponsored_premium || 0)
      ),
      conversionRate: totalUsers && totalUsers > 0 
        ? ((
            (planCounts?.sponsor_standard || 0) + 
            (planCounts?.sponsor_premium || 0) + 
            (planCounts?.sponsored_premium || 0) +
            (planCounts?.standard || 0) +
            (planCounts?.premium || 0)
          ) / totalUsers) * 100
        : 0,
    },
  };

  return {
    planStats,
    recentActivity: recentActivity || [],
    loading: countsLoading || revenueLoading || conversionsLoading || totalLoading || activityLoading,
  };
};
